import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Pagination, InputBase, Select, MenuItem, FormControl, useTheme, useMediaQuery } from "@mui/material";
import { getStationOutstanding, getOutstandingStations } from "api/financial";
import { useSetBreadcrumb } from "hooks/BreadcrumbContext";

const FONT = "'Plus Jakarta Sans', 'Noto Sans Lao', system-ui, sans-serif";
const MONO = "'Space Grotesk', 'Noto Sans Lao', ui-monospace, monospace";
const AVATARS = ["#6d28d9", "#7c3aed", "#5b21b6", "#4338ca", "#3730a3", "#8b5cf6", "#7e22ce"];
const fmt = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const initials = (name) => {
  const p = (name || "?").trim().split(/\s+/);
  return (p.length >= 2 ? p[0][0] + p[1][0] : (name || "?").slice(0, 2)).toUpperCase();
};
const labelSx = { fontSize: "10.5px", letterSpacing: ".14em", color: "#a8a3b4", fontWeight: 700 };
const headCell = { fontSize: 11, fontWeight: 700, letterSpacing: ".1em", color: "#a8a3b4", textTransform: "uppercase" };
const GRID = "2.4fr 1fr 1.8fr";

export default function OutstandingDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { stationId } = useParams();

  const [rows, setRows] = useState([]);
  const [grand, setGrand] = useState({ grandTotal: 0, totalCustomers: 0, totalParcels: 0 });
  const [meta, setMeta] = useState({ pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [stations, setStations] = useState([]);

  // Station list for the switcher (stations that have outstanding parcels).
  useEffect(() => {
    getOutstandingStations().then((res) => setStations(res && res.success ? res.data : []));
  }, []);
  // Reset paging/search when switching station.
  useEffect(() => { setPage(1); setSearch(""); }, [stationId]);

  const load = useCallback(async () => {
    const res = await getStationOutstanding(stationId, { page, limit: 20, search });
    setRows(res && res.success ? res.data : []);
    setMeta((res && res.pagination) || { pages: 1, total: 0 });
    setGrand({ grandTotal: (res && res.grandTotal) || 0, totalCustomers: (res && res.totalCustomers) || 0, totalParcels: (res && res.totalParcels) || 0 });
  }, [stationId, page, search]);
  useEffect(() => { load(); }, [load]);

  useSetBreadcrumb([
    { label: t("financial.title"), to: "/financial" },
    { label: `${t("financial.outstandingByStation")} · ${stationId}` },
  ]);

  const maxFee = Math.max(...rows.map((r) => r.totalShippingFee || 0), 1);
  const goParcels = (r) => navigate(`/financial/outstanding/${encodeURIComponent(stationId)}/${r.customerId}`);

  return (
    <Box sx={{ fontFamily: FONT, maxWidth: 1280, mr: "auto", p: { xs: "20px", md: "28px 40px 60px" } }}>
      {/* title + station switcher */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "14px" }}>
        <Box>
          <Box component="h1" sx={{ m: 0, fontSize: 26, fontWeight: 800, color: "#16131f", letterSpacing: "-.01em" }}>{t("financial.outstandingByStation")}</Box>
          <Box component="p" sx={{ m: "4px 0 0", fontSize: 13, color: "#9b96a6" }}>{t("financial.outstandingSubtitle")}</Box>
        </Box>
        <FormControl size="small">
          <Select
            value={stationId}
            onChange={(e) => navigate(`/financial/outstanding/${encodeURIComponent(e.target.value)}`)}
            sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 14, minWidth: 200, borderRadius: "11px", background: "#fff", boxShadow: "0 1px 2px rgba(20,18,30,.04)", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ece9f1" }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ddd0fa" }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" } }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 360, borderRadius: "12px" } } }}
          >
            {(stations.some((s) => s.station === stationId) ? stations : [{ station: stationId, stationName: stationId, totalShippingFee: grand.grandTotal }, ...stations]).map((s) => (
              <MenuItem key={s.station} value={s.station} sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 600 }}>
                {s.stationName}
                <Box component="span" sx={{ ml: "auto", pl: 2, fontFamily: MONO, fontSize: 12.5, color: "#9b96a6" }}>฿{fmt(s.totalShippingFee)}</Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* KPI strip */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4,1fr)" }, gap: "16px", mt: "22px" }}>
        <Box sx={{ background: "linear-gradient(155deg,#fff 0%,#f5efff 100%)", border: "1px solid #ece4fb", borderRadius: "18px", p: "18px 20px", boxShadow: "0 1px 2px rgba(20,18,30,.04),0 14px 28px -20px rgba(86,40,160,.22)" }}>
          <Box sx={labelSx}>{t("financial.station").toUpperCase()}</Box>
          <Box sx={{ fontFamily: MONO, fontSize: 24, fontWeight: 700, color: "#161420", mt: "10px" }}>{stationId}</Box>
        </Box>
        <Kpi label={t("financial.totalOutstanding")} value={`฿${fmt(grand.grandTotal)}`} color="#6d28d9" />
        <Kpi label={t("financial.totalCustomers")} value={fmt(grand.totalCustomers)} />
        <Kpi label={t("financial.parcels")} value={fmt(grand.totalParcels)} />
      </Box>

      {/* filter */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mt: "22px", flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 200, maxWidth: 340 }}>
          <InputBase fullWidth placeholder={`${t("financial.customer")}…`} value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            sx={{ fontFamily: FONT, fontSize: 13, px: "14px", py: "9px", borderRadius: "11px", border: "1px solid #ece9f1", background: "#fff", boxShadow: "0 1px 2px rgba(20,18,30,.04)" }} />
        </Box>
        <Box sx={{ ml: "auto", fontSize: "12.5px", color: "#a8a3b4", fontWeight: 600 }}>{meta.total || 0} {t("financial.results")}</Box>
      </Box>

      {/* list */}
      {rows.length === 0 ? (
        <Box sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "22px", mt: "16px", p: "50px 24px", textAlign: "center", color: "#b9b4c4" }}>{t("financial.noData")}</Box>
      ) : isMobile ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", mt: "16px" }}>
          {rows.map((r, i) => (
            <Box key={r.customerId} onClick={() => goParcels(r)} sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "16px", p: "14px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: "12px" }}>
              <Box sx={{ width: 38, height: 38, flex: "none", borderRadius: "11px", background: AVATARS[i % AVATARS.length], color: "#fff", fontFamily: MONO, fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>{initials(r.customer?.name)}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontSize: 14, fontWeight: 700, color: "#1c1a26" }}>{r.customer?.name || "—"}</Box>
                <Box sx={{ fontSize: 12, color: "#9b96a6" }}>{r.count} {t("financial.parcels")}</Box>
              </Box>
              <Box sx={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, color: "#161420" }}>฿{fmt(r.totalShippingFee)}</Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "22px", overflow: "hidden", mt: "16px", boxShadow: "0 1px 2px rgba(20,18,30,.04),0 16px 40px -28px rgba(20,18,30,.18)" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: GRID, alignItems: "center", p: "13px 24px", background: "#faf9fc", borderBottom: "1.5px solid #ece9f1" }}>
            <Box sx={headCell}>{t("financial.customer")}</Box>
            <Box sx={{ ...headCell, textAlign: "center" }}>{t("financial.parcels")}</Box>
            <Box sx={{ ...headCell, color: "#6d28d9", textAlign: "right" }}>{t("financial.shippingFeeTHB")}</Box>
          </Box>
          {rows.map((r, i) => (
            <Box key={r.customerId} onClick={() => goParcels(r)}
              sx={{ display: "grid", gridTemplateColumns: GRID, alignItems: "center", p: "0 24px", borderTop: "1px solid #f4f2f8", minHeight: 60, cursor: "pointer", "&:hover": { background: "#faf9fc" } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px", py: "14px" }}>
                <Box sx={{ width: 36, height: 36, flex: "none", borderRadius: "11px", background: AVATARS[i % AVATARS.length], color: "#fff", fontFamily: MONO, fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>{initials(r.customer?.name)}</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ fontSize: 14, fontWeight: 700, color: "#1c1a26", lineHeight: 1.2 }}>{r.customer?.name || "—"}</Box>
                  {r.customer?.whatsapp && <Box sx={{ fontSize: "11.5px", color: "#9b96a6", fontFamily: MONO, mt: "2px" }}>{r.customer.whatsapp}</Box>}
                </Box>
              </Box>
              <Box sx={{ textAlign: "center", fontFamily: MONO, fontSize: 14, color: "#6b6775" }}>{r.count}</Box>
              <Box sx={{ textAlign: "right", pr: "4px" }}>
                <Box sx={{ fontFamily: MONO, fontSize: 15, fontWeight: 600, color: "#161420" }}>฿{fmt(r.totalShippingFee)}</Box>
                <Box sx={{ height: 5, borderRadius: "3px", background: "#f0eef5", mt: "6px", overflow: "hidden", maxWidth: 130, ml: "auto" }}>
                  <Box sx={{ height: "100%", borderRadius: "3px", background: "linear-gradient(90deg,#8b5cf6,#6d28d9)", width: `${Math.min((r.totalShippingFee / maxFee) * 100, 100)}%` }} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {meta.pages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
          <Pagination color="secondary" count={meta.pages} page={page} onChange={(e, v) => setPage(v)} />
        </Box>
      )}
    </Box>
  );
}

function Kpi({ label, value, color }) {
  return (
    <Box sx={{ background: "#fff", border: "1px solid #ece9f1", borderRadius: "18px", p: "18px 20px", boxShadow: "0 1px 2px rgba(20,18,30,.04),0 14px 28px -20px rgba(20,18,30,.12)" }}>
      <Box sx={labelSx}>{label.toUpperCase()}</Box>
      <Box sx={{ fontFamily: MONO, fontSize: 24, fontWeight: 700, color: color || "#161420", mt: "10px" }}>{value}</Box>
    </Box>
  );
}
